import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useDataUploadsActions } from "@ryde/redux";
import {
  errors,
  ETL_RUN_DATE_TIME_FORMAT,
  formatDate,
  IDataUploadLogRecord,
  NO_VALUE_DISPLAY,
  RichLogLevel,
  useTimezone,
  warnAndToastSimpleResultWithFallback,
} from "@ryde/shared";

import { MuiButton } from "../../controls";
import { DataUploadLogViewerStyled } from "./data-upload-log-viewer.styled";

interface IDataUploadLogViewerProps {
  dataUploadId?: number;
}

/**
 * "View last run log" for the customer Data Uploads panel. The backend has already filtered the records
 * to Info/Warn lines and redacted urls/credentials, so this renders them and applies no safety logic.
 *
 * Fetched on demand, and only for a saved upload — a not-yet-run row has no id and nothing to show.
 */
export const DataUploadLogViewer: FC<IDataUploadLogViewerProps> = ({
  dataUploadId,
}) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { fetchLastRunLog } = useDataUploadsActions();
  const [records, setRecords] = useState<IDataUploadLogRecord[] | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // The panel is not guaranteed to remount when the selected row changes, so drop a log that belongs
  // to the previous upload rather than showing it under the new one.
  useEffect(() => {
    setRecords(undefined);
    setIsOpen(false);
  }, [dataUploadId]);

  const handleToggle = useEvent(async (): Promise<void> => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    setIsOpen(true);

    if (!dataUploadId) {
      return;
    }

    // Re-fetched on every open, not cached: a run started from this same panel finishes while it is
    // still mounted, and the cached log would then be the previous run's.
    setIsLoading(true);

    try {
      const result = await fetchLastRunLog(dataUploadId);

      if (!result.success) {
        // Collapse on failure rather than leaving an empty box; the toast says why.
        setIsOpen(false);
        warnAndToastSimpleResultWithFallback(
          result,
          t,
          errors.dataUpload.failedToLoadLog,
        );
        return;
      }

      setRecords(result.data?.records ?? []);
    } finally {
      // Cleared on every exit — a failed fetch must not leave the section spinning.
      setIsLoading(false);
    }
  });

  if (!dataUploadId) {
    return null;
  }

  return (
    <DataUploadLogViewerStyled>
      <MuiButton
        className="log__toggle"
        muiVariant="outlined"
        muiSize="small"
        muiStyle="secondary"
        e2eData="data-upload-view-log"
        disabled={isLoading}
        aria-expanded={isOpen}
        onClick={handleToggle}
      >
        {t(isOpen ? "dataUpload.hideLog" : "dataUpload.viewLog")}
      </MuiButton>

      {isOpen && !isLoading && records?.length === 0 && (
        <span className="log__empty" aria-live="polite">
          {t("dataUpload.noLog")}
        </span>
      )}

      {isOpen && !!records?.length && (
        <div className="log__list" aria-live="polite">
          {records.map((record, index) => (
            <div
              key={`${record.timestamp ?? "no-time"}-${index}`}
              className={`log__row${record.level === RichLogLevel.Warn ? " log__row--warn" : ""}`}
            >
              <span className="log__time">
                {record.timestamp
                  ? formatDate({
                      dateISO: record.timestamp,
                      dateFormat: ETL_RUN_DATE_TIME_FORMAT,
                      timezone,
                    })
                  : NO_VALUE_DISPLAY}
              </span>
              {record.step && (
                <span className="log__step">
                  {t(`dataUpload.logStep.${record.step}`)}
                </span>
              )}
              <span className="log__message">{record.message}</span>
            </div>
          ))}
        </div>
      )}
    </DataUploadLogViewerStyled>
  );
};
